import { BILLING_CYCLES, CHECKOUT_STEPS, DEFAULT_SELECTION, PRODUCTS } from 'lib/billing/config';

export type CheckoutStep = (typeof CHECKOUT_STEPS)[keyof typeof CHECKOUT_STEPS];
export type BillingCycle = (typeof BILLING_CYCLES)[keyof typeof BILLING_CYCLES];
export type Product = (typeof PRODUCTS)[keyof typeof PRODUCTS];

// DEFAULT_SELECTION lives in a .js file, so its empty parallel arrays infer as
// never[]. Override those five fields with their real element types.
export type Selection = Omit<
  typeof DEFAULT_SELECTION,
  'dedicated' | 'dedicatedSubIds' | 'dedicatedSubItemIds' | 'dedicatedNicknames' | 'dedicatedSubLineCounts'
> & {
  dedicated: number[];
  dedicatedSubIds: (string | null)[];
  dedicatedSubItemIds: (string | null)[];
  dedicatedNicknames: (string | null)[];
  dedicatedSubLineCounts: number[];
};
export type SelectionPatch = Partial<Selection> | ((prev: Selection) => Selection);

export interface CheckoutForm {
  email: string;
  firstName: string;
  lastName: string;
}

export type CheckoutCompletePayload = {
  subscriptionId?: string;
  status?: string;
  userId?: string;
  [k: string]: unknown;
};

export type CheckoutCompleteHandler = (payload: CheckoutCompletePayload) => void;

/**
 * When set, the Payment step settles this overdue invoice (Card or Crypto)
 * instead of building a plan. The Configure step still shows the plan.
 */
export interface OverdueInvoice {
  invoiceId: string;
  subscriptionId: string;
  amountDueCents: number;
  currency?: string;
}

/** Verification verdict for the applied discount code (lib/billing/discountCode). */
export type CouponStatus = 'valid' | 'invalid' | 'unknown' | 'pending';

/**
 * Normalized result of validating the applied coupon against the public
 * billing endpoint (GET /public/api/v1/billing/discount-code). 'pending'
 * means the round-trip is in flight (set synchronously on Apply); 'unknown'
 * covers everything that couldn't actually be verified — transport failure or
 * base URL unconfigured — and the UI then falls back to inferring validity
 * from the invoice preview. Amounts are informational: `preview.discount`
 * stays authoritative for what is charged.
 */
export interface CouponInfo {
  status: CouponStatus;
  code?: string;
  percentOff: number;
  amountOff: number;
  currency: string;
  duration: string;
  /**
   * Product restriction from the coupon's applies_to. Empty = unrestricted —
   * the code discounts every product. `appliesToProducts` carries raw Stripe
   * product ids; `appliesToProductIds` the same restriction as catalog
   * product uuids (the ids the price catalog exposes).
   */
  appliesToProducts?: string[];
  appliesToProductIds?: string[];
  /**
   * Price restriction (applies_to_prices metadata on the Stripe coupon or
   * promotion code, enforced server-side — Stripe can only restrict by
   * product natively). Empty = valid for every price. Stripe price ids and
   * their catalog price uuids respectively.
   */
  appliesToPrices?: string[];
  appliesToPriceIds?: string[];
}

/** How the customer pays in the drawer. A crypto payment never uses a cancel offer, so its quote leaves it out. */
export const PAYMENT_TAB = { CARD: 'card', CRYPTO: 'crypto' } as const;
export type PaymentTab = (typeof PAYMENT_TAB)[keyof typeof PAYMENT_TAB];

export interface CheckoutState {
  selection: Selection;
  step: CheckoutStep;
  form: CheckoutForm;
  submitting: boolean;
  checkoutSession: unknown | null;
  drawerOpen: boolean;
  /** Stripe coupon NAME typed by the customer at checkout (e.g. "SAVE30"). */
  coupon: string;
  /** The payment tab chosen; back to card whenever the customer leaves Payment. */
  paymentTab: PaymentTab;
  /**
   * Server verdict for `coupon`, written by useCouponValidationSync. null when
   * no code is applied or a fresh code hasn't been verified yet (treated the
   * same as status 'unknown'). Cleared alongside the coupon (setCoupon/reset).
   */
  couponInfo: CouponInfo | null;
  onComplete: CheckoutCompleteHandler | null;
  /**
   * When the current drawer session completed a checkout (PaymentStep success),
   * else null. openDrawer resets it, so on close it says whether THIS session
   * paid — useCheckoutLauncher hands that to onClosed.
   */
  completedAt: number | null;
  /**
   * One-shot entry step for the next drawer open. openDrawer(step) records it;
   * CheckoutDrawer's open-effect consumes (and clears) it instead of forcing
   * Configure — that's how pre-configured flows (DirectBuyMcpModal) land
   * straight on Payment while a plain reopen still defaults to Configure.
   */
  requestedStep: CheckoutStep | null;
  // One-shot DE kind for a "Purchase Additional Env" flow: set before openDrawer(),
  // appended by CheckoutProvider after hydration, then cleared. null = none.
  pendingNewEnvKind: string | null;
  // Invoice-pay mode: non-null while the drawer is settling an overdue invoice.
  overdueInvoice: OverdueInvoice | null;
  /**
   * "This open came from a Subscribe-to-Analyzer entry point." Bumped by the
   * launcher; AddonsSection scrolls the Analyzer card into view and flashes it
   * once per token. Deliberately does NOT pre-enable the add-on in the cart —
   * highlighting states the intent, the customer opts in. A counter (not a
   * boolean) so repeat clicks re-trigger the flash. 0 = never requested.
   */
  analyzerFocusToken: number;
  /**
   * One-shot selection patch from the quick-subscribe dialog: applied by
   * CheckoutProvider AFTER hydration (hydration replaces the whole selection,
   * so a patch staged directly onto the store would be clobbered for any
   * user who already has active subscriptions), then cleared.
   */
  pendingQuickPatch: Record<string, unknown> | null;
}

export interface CheckoutActions {
  setSelection: (patch: SelectionPatch) => void;
  setForm: (patch: Partial<CheckoutForm>) => void;
  setStep: (step: CheckoutStep) => void;
  goToConfigure: () => void;
  goToPayment: () => void;
  setSubmitting: (v: boolean) => void;
  setCheckoutSession: (s: unknown | null) => void;
  setCoupon: (coupon: string) => void;
  setPaymentTab: (paymentTab: PaymentTab) => void;
  setCouponInfo: (couponInfo: CouponInfo | null) => void;
  openDrawer: (step?: CheckoutStep) => void;
  closeDrawer: () => void;
  resetSelection: () => void;
  setPendingNewEnvKind: (kind: string | null) => void;
  requestAnalyzerFocus: () => void;
  setPendingQuickPatch: (patch: Record<string, unknown> | null) => void;
  setOnComplete: (fn: CheckoutCompleteHandler | null) => void;
  markCompleted: () => void;
  setOverdueInvoice: (inv: OverdueInvoice | null) => void;
  openOverdueDrawer: (overdueInvoice: OverdueInvoice, onComplete: CheckoutCompleteHandler) => void;
  reset: () => void;
}

export type BillingSubscription = Record<string, unknown>;
export type BillingOverview = Record<string, unknown> & { addOns?: string[] };
export type BillingCatalog = {
  raw: unknown;
  get: (product: unknown, cycle: unknown) => unknown;
  has: (product: unknown, cycle: unknown) => boolean;
  getById: (id: unknown) => unknown;
  listForProduct: (product: unknown) => unknown[];
};

export interface BillingState {
  subscriptions: BillingSubscription[];
  overview: BillingOverview | null;
  catalog: BillingCatalog | null;
  loading: boolean;
  catalogLoading: boolean;
  error: unknown | null;
  catalogError: unknown | null;
  lastFetched: number | null;
}

/**
 * Loose shape of the billing gRPC client. We accept any client whose method
 * signatures are call-compatible — typing the responses as `unknown` lets the
 * store consume the existing `BillingClient` (whose generated methods return
 * `Promise<unknown>`) without per-callsite casts. Responses are narrowed by
 * the proto adapter helpers (`subscriptionsFromPb` etc.) before the store
 * stores them.
 */
export interface BillingClientLike {
  listSubscriptions: (req: { userId: string; statusList: string[] }) => Promise<unknown>;
  listPrices: (req: { userId: string }) => Promise<unknown>;
  billingOverview: (req: { userId: string }) => Promise<unknown>;
}

export type BillingClientFactory = () => Promise<BillingClientLike>;

export interface BillingActions {
  fetch: (userId: string, opts?: { force?: boolean }) => Promise<void>;
  fetchCatalog: (userId: string) => Promise<void>;
  invalidate: () => void;
  applySubscriptionUpdate: (sub: { id: string } & Record<string, unknown>) => void;
  reset: () => void;
}
